import { useEffect, useRef, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { supabase } from '../utils/supabase';
import { useAuth } from '../contexts/AuthContext';

// Generate browser fingerprint for better unique visitor tracking
const generateFingerprint = (): string => {
  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d');
  let canvasHash = '';
  
  if (ctx) {
    ctx.textBaseline = 'top';
    ctx.font = '14px Arial';
    ctx.fillText('fingerprint', 2, 2);
    canvasHash = canvas.toDataURL().slice(-50);
  }

  const components = [
    navigator.userAgent,
    navigator.language,
    window.screen.width + 'x' + window.screen.height,
    window.screen.colorDepth,
    new Date().getTimezoneOffset(),
    navigator.hardwareConcurrency || 0,
    navigator.maxTouchPoints || 0,
    canvasHash,
  ];

  // Simple hash function
  const str = components.join('|');
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    const char = str.charCodeAt(i);
    hash = ((hash << 5) - hash) + char;
    hash = hash & hash;
  }
  return Math.abs(hash).toString(36);
};

// Get or create visitor ID (stored in localStorage for persistence)
const getVisitorId = (): string => {
  let visitorId = localStorage.getItem('desta_visitor_id');
  if (!visitorId) {
    // Combine fingerprint with random component for uniqueness
    const fingerprint = generateFingerprint();
    visitorId = `${fingerprint}-${Date.now().toString(36)}`;
    localStorage.setItem('desta_visitor_id', visitorId);
  }
  return visitorId;
};

// Generate or get session ID (for tracking sessions within a visit)
const getSessionId = (): string => {
  let sessionId = sessionStorage.getItem('desta_session_id');
  if (!sessionId) {
    sessionId = `${Date.now()}-${Math.random().toString(36).substring(2, 15)}`;
    sessionStorage.setItem('desta_session_id', sessionId);
  }
  return sessionId;
};

// Get page title from path
const getPageTitle = (path: string): string => {
  const titles: Record<string, string> = {
    '/': 'Home',
    '/team': 'Team',
    '/academy': 'Academy',
    '/login': 'Login',
    '/register': 'Register',
    '/forgot-password': 'Forgot Password',
    '/reset-password': 'Reset Password',
    '/chat': 'AI Chat',
    '/dashboard': 'Dashboard',
    '/assessments': 'Assessments',
    '/academy/free-module': 'Free Module',
    '/academy/single-module': 'Single Module',
    '/academy/full-program': 'Full Program',
    '/academy/agents-automation': 'AI Agents & Automation',
    '/academy/next-steps': 'Next Steps',
    '/academy/ui-basics': 'UI Basics',
    '/academy/web-scraping': 'Web Scraping',
    '/payment': 'Payment',
  };
  return titles[path] || path;
};

interface PageViewData {
  page_path: string;
  page_title: string;
  referrer: string | null;
  user_agent: string;
  screen_width: number;
  screen_height: number;
  language: string;
  user_id: string | null;
  session_id: string;
  visitor_id: string;
}

/**
 * Hook to track page views
 * Automatically tracks page changes and sends data to Supabase
 */
export const usePageTracking = () => {
  const location = useLocation();
  const { user } = useAuth();
  const lastTrackedPath = useRef<string | null>(null);

  const trackPageView = useCallback(async (path: string) => {
    // Prevent duplicate tracking of the same page
    if (lastTrackedPath.current === path) return;
    lastTrackedPath.current = path;

    const pageViewData: PageViewData = {
      page_path: path,
      page_title: getPageTitle(path),
      referrer: document.referrer || null,
      user_agent: navigator.userAgent,
      screen_width: window.screen.width,
      screen_height: window.screen.height,
      language: navigator.language,
      user_id: user?.id || null,
      session_id: getSessionId(),
      visitor_id: getVisitorId(),
    };

    try {
      const { error } = await supabase
        .from('page_views')
        .insert([pageViewData]);

      if (error) {
        console.error('📊 Analytics error:', error);
      } else {
        console.log('📊 Page view tracked:', path);
      }
    } catch (err) {
      console.error('📊 Analytics error:', err);
    }
  }, [user]);

  useEffect(() => {
    trackPageView(location.pathname);
  }, [location.pathname, trackPageView]);

  return { trackPageView };
};

// ============================================
// ANALYTICS DATA HOOKS (for Admin Dashboard)
// ============================================

export interface DailyStats {
  date: string;
  total_views: number;
  unique_sessions: number;
  unique_visitors: number;
}

export interface PageStats {
  page_path: string;
  views: number;
  unique_visitors: number;
}

export interface TodayStats {
  total_views: number;
  unique_sessions: number;
  unique_visitors: number;
  top_pages: Array<{ page_path: string; views: number }>;
}

/**
 * Hook to get today's page view statistics (Admin only)
 */
export const useTodayStats = () => {
  const fetchTodayStats = useCallback(async (): Promise<TodayStats | null> => {
    try {
      const { data, error } = await supabase.rpc('get_today_page_views');
      
      if (error) {
        console.error('📊 Error fetching today stats:', error);
        return null;
      }
      
      if (data && data.length > 0) {
        return {
          total_views: data[0].total_views || 0,
          unique_sessions: data[0].unique_sessions || 0,
          unique_visitors: data[0].unique_visitors || 0,
          top_pages: data[0].top_pages || [],
        };
      }
      
      return { total_views: 0, unique_sessions: 0, unique_visitors: 0, top_pages: [] };
    } catch (err) {
      console.error('📊 Error fetching today stats:', err);
      return null;
    }
  }, []);

  return { fetchTodayStats };
};

/**
 * Hook to get page view statistics for a date range (Admin only)
 */
export const usePageViewStats = () => {
  const fetchDailyStats = useCallback(async (
    startDate?: Date,
    endDate?: Date
  ): Promise<DailyStats[]> => {
    try {
      const start = startDate || new Date(Date.now() - 7 * 24 * 60 * 60 * 1000);
      const end = endDate || new Date();

      const { data, error } = await supabase.rpc('get_page_views_stats', {
        start_date: start.toISOString(),
        end_date: end.toISOString(),
      });

      if (error) {
        console.error('📊 Error fetching daily stats:', error);
        return [];
      }

      return data || [];
    } catch (err) {
      console.error('📊 Error fetching daily stats:', err);
      return [];
    }
  }, []);

  const fetchPageStats = useCallback(async (
    startDate?: Date,
    endDate?: Date
  ): Promise<PageStats[]> => {
    try {
      const start = startDate || new Date(Date.now() - 7 * 24 * 60 * 60 * 1000);
      const end = endDate || new Date();

      const { data, error } = await supabase.rpc('get_page_views_by_path', {
        start_date: start.toISOString(),
        end_date: end.toISOString(),
      });

      if (error) {
        console.error('📊 Error fetching page stats:', error);
        return [];
      }

      return data || [];
    } catch (err) {
      console.error('📊 Error fetching page stats:', err);
      return [];
    }
  }, []);

  return { fetchDailyStats, fetchPageStats };
};

export default usePageTracking;
